import { useAuthActions } from '@convex-dev/auth/react'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { revalidateLogic } from '@tanstack/react-form'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { IconEyeOutline18, IconEyeSlashOutline18 } from 'nucleo-ui-outline-18'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AuthLayout } from '~/components/auth/auth-layout'
import { Button } from '~/components/ui/button'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '~/components/ui/field'
import { Input } from '~/components/ui/input'
import { Spinner } from '~/components/ui/spinner'
import { makePasswordResetRequestSchema, makePasswordResetVerificationSchema } from '~/lib/auth'
import { useAppForm } from '~/lib/form'
import { useLocale } from '~/lib/locale-store'
import { titleFor } from '~/lib/route-title'
import { ensureAuth } from '~/lib/router-auth'
import { usePostAuthNavigation } from '~/lib/use-post-auth-navigation'
import { PASSWORD_RESET_CODE_LENGTH } from '~convex/lib/password'

type Step = 'request' | 'verify'

export const Route = createFileRoute('/forgot-password')({
  beforeLoad: async () => {
    const auth = await ensureAuth()
    if (auth.isAuthenticated) {
      throw redirect({ to: '/home' })
    }
  },
  component: RouteComponent,
  head: () => ({
    meta: [
      { title: titleFor(msg`Reset password`) },
      { content: 'noindex, nofollow', name: 'robots' },
    ],
  }),
  ssr: false,
  validateSearch: (search: Record<string, unknown>): { email?: string } =>
    typeof search.email === 'string' ? { email: search.email } : {},
})

function RouteComponent() {
  const { t, i18n } = useLingui()
  const { signIn } = useAuthActions()
  const { beginPostAuthNavigation, isAwaitingAuthentication } = usePostAuthNavigation('/home')
  const { locale } = useLocale()
  const { email: initialEmail = '' } = Route.useSearch()
  const [step, setStep] = useState<Step>('request')
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [resendPending, setResendPending] = useState(false)
  const requestSchema = useMemo(() => makePasswordResetRequestSchema(i18n), [i18n])
  const verificationSchema = useMemo(() => makePasswordResetVerificationSchema(i18n), [i18n])

  const requestCode = async (email: string): Promise<boolean> => {
    try {
      await signIn('password', {
        email,
        flow: 'reset',
        redirectTo: `/forgot-password?locale=${encodeURIComponent(locale)}`,
      })
    } catch (error) {
      // Account lookup failures are deliberately indistinguishable from
      // success so this public form cannot reveal registered addresses.
      console.error('Password reset request error:', error)
      if (!navigator.onLine) {
        toast.error(t`You're offline. Check your connection and try again.`)
        return false
      }
    }
    return true
  }

  const requestForm = useAppForm({
    defaultValues: { email: initialEmail },
    onSubmit: async ({ value }) => {
      const email = value.email.trim().toLowerCase()
      if (await requestCode(email)) {
        setSubmittedEmail(email)
        setStep('verify')
      }
    },
    validationLogic: revalidateLogic(),
    validators: { onDynamic: requestSchema },
  })

  const verificationForm = useAppForm({
    defaultValues: { code: '', confirmPassword: '', password: '' },
    onSubmit: async ({ value }) => {
      try {
        const result = await signIn('password', {
          code: value.code,
          email: submittedEmail,
          flow: 'reset-verification',
          newPassword: value.password,
        })
        if (!result.signingIn) {
          throw new Error('Password reset did not create a session')
        }
        beginPostAuthNavigation()
      } catch (error) {
        console.error('Password reset verification error:', error)
        toast.error(
          navigator.onLine
            ? t`That code is invalid or has expired. Request a new one and try again.`
            : t`You're offline. Check your connection and try again.`
        )
      }
    },
    validationLogic: revalidateLogic(),
    validators: { onDynamic: verificationSchema },
  })

  const handleResend = async () => {
    setResendPending(true)
    const requested = await requestCode(submittedEmail)
    if (requested) {
      toast.success(t`If a password account exists for this email, a new code is on its way.`)
    }
    setResendPending(false)
  }

  return (
    <AuthLayout
      altPrompt={
        <Trans>
          Remembered your password?{' '}
          <Link
            className="font-medium text-foreground underline-offset-4 transition-colors hover:underline"
            to="/sign-in"
          >
            Sign in
          </Link>
        </Trans>
      }
      subtitle={
        step === 'request' ? (
          <Trans>Enter your account email and we'll send you a code</Trans>
        ) : (
          <Trans>Enter the code sent to {submittedEmail}</Trans>
        )
      }
      title={
        step === 'request' ? <Trans>Reset your password</Trans> : <Trans>Check your inbox</Trans>
      }
    >
      {step === 'request' ? (
        <form
          className="animate-fade-up space-y-6"
          onSubmit={(event) => {
            event.preventDefault()
            event.stopPropagation()
            requestForm.handleSubmit()
          }}
        >
          <FieldGroup>
            <requestForm.Field name="email">
              {(field) => (
                <Field>
                  <FieldLabel className="font-medium text-sm" htmlFor={field.name}>
                    <Trans>Email</Trans>
                  </FieldLabel>
                  <Input
                    aria-describedby={`${field.name}-error`}
                    aria-invalid={field.state.meta.errors.length > 0}
                    autoComplete="email"
                    autoFocus
                    className="h-11 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/20"
                    id={field.name}
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    placeholder={t`you@email.com`}
                    type="email"
                    value={field.state.value}
                  />
                  <FieldError
                    className="text-xs"
                    errors={field.state.meta.errors}
                    id={`${field.name}-error`}
                  />
                </Field>
              )}
            </requestForm.Field>
            <requestForm.Subscribe>
              {(state) => (
                <Button
                  className="h-11 w-full font-medium"
                  disabled={!state.canSubmit || state.isSubmitting || isAwaitingAuthentication}
                  type="submit"
                >
                  {state.isSubmitting || isAwaitingAuthentication ? (
                    <span className="flex items-center gap-2">
                      <Spinner className="size-4 text-current" />
                      <Trans>Sending code...</Trans>
                    </span>
                  ) : (
                    <Trans>Send reset code</Trans>
                  )}
                </Button>
              )}
            </requestForm.Subscribe>
          </FieldGroup>
        </form>
      ) : (
        <form
          className="animate-fade-up space-y-6"
          onSubmit={(event) => {
            event.preventDefault()
            event.stopPropagation()
            verificationForm.handleSubmit()
          }}
        >
          <FieldGroup>
            <verificationForm.Field name="code">
              {(field) => (
                <Field>
                  <FieldLabel className="font-medium text-sm" htmlFor={field.name}>
                    <Trans>Reset code</Trans>
                  </FieldLabel>
                  <Input
                    aria-describedby={`${field.name}-error`}
                    aria-invalid={field.state.meta.errors.length > 0}
                    autoComplete="one-time-code"
                    autoFocus
                    className="h-11 font-mono tracking-[0.2em] transition-shadow focus-visible:ring-2 focus-visible:ring-ring/20"
                    id={field.name}
                    inputMode="numeric"
                    maxLength={PASSWORD_RESET_CODE_LENGTH}
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value.replace(/\D/g, ''))}
                    placeholder="00000000"
                    value={field.state.value}
                  />
                  <FieldError
                    className="text-xs"
                    errors={field.state.meta.errors}
                    id={`${field.name}-error`}
                  />
                </Field>
              )}
            </verificationForm.Field>

            <verificationForm.Field name="password">
              {(field) => (
                <Field>
                  <FieldLabel className="font-medium text-sm" htmlFor={field.name}>
                    <Trans>New password</Trans>
                  </FieldLabel>
                  <div className="relative">
                    <Input
                      aria-describedby={`${field.name}-desc ${field.name}-error`}
                      aria-invalid={field.state.meta.errors.length > 0}
                      autoComplete="new-password"
                      className="h-11 pr-10 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/20"
                      id={field.name}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={(event) => field.handleChange(event.target.value)}
                      placeholder="••••••••"
                      type={showPassword ? 'text' : 'password'}
                      value={field.state.value}
                    />
                    <Button
                      aria-label={showPassword ? t`Hide password` : t`Show password`}
                      className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowPassword((visible) => !visible)}
                      size="icon-sm"
                      type="button"
                      variant="ghost"
                    >
                      {showPassword ? (
                        <IconEyeSlashOutline18 className="size-5" />
                      ) : (
                        <IconEyeOutline18 className="size-5" />
                      )}
                    </Button>
                  </div>
                  <FieldDescription className="text-xs" id={`${field.name}-desc`}>
                    <Trans>At least 8 characters with one number or special character</Trans>
                  </FieldDescription>
                  <FieldError
                    className="text-xs"
                    errors={field.state.meta.errors}
                    id={`${field.name}-error`}
                  />
                </Field>
              )}
            </verificationForm.Field>

            <verificationForm.Field name="confirmPassword">
              {(field) => (
                <Field>
                  <FieldLabel className="font-medium text-sm" htmlFor={field.name}>
                    <Trans>Confirm new password</Trans>
                  </FieldLabel>
                  <div className="relative">
                    <Input
                      aria-describedby={`${field.name}-error`}
                      aria-invalid={field.state.meta.errors.length > 0}
                      autoComplete="new-password"
                      className="h-11 pr-10 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/20"
                      id={field.name}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={(event) => field.handleChange(event.target.value)}
                      placeholder="••••••••"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={field.state.value}
                    />
                    <Button
                      aria-label={showConfirmPassword ? t`Hide password` : t`Show password`}
                      className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowConfirmPassword((visible) => !visible)}
                      size="icon-sm"
                      type="button"
                      variant="ghost"
                    >
                      {showConfirmPassword ? (
                        <IconEyeSlashOutline18 className="size-5" />
                      ) : (
                        <IconEyeOutline18 className="size-5" />
                      )}
                    </Button>
                  </div>
                  <FieldError
                    className="text-xs"
                    errors={field.state.meta.errors}
                    id={`${field.name}-error`}
                  />
                </Field>
              )}
            </verificationForm.Field>

            <verificationForm.Subscribe>
              {(state) => (
                <Button
                  className="h-11 w-full font-medium"
                  disabled={!state.canSubmit || state.isSubmitting}
                  type="submit"
                >
                  {state.isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <Spinner className="size-4 text-current" />
                      <Trans>Resetting password...</Trans>
                    </span>
                  ) : (
                    <Trans>Reset password</Trans>
                  )}
                </Button>
              )}
            </verificationForm.Subscribe>

            <div className="flex items-center justify-center gap-3 text-xs">
              <button
                className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-50"
                disabled={resendPending}
                onClick={handleResend}
                type="button"
              >
                {resendPending ? <Trans>Sending...</Trans> : <Trans>Send a new code</Trans>}
              </button>
              <span className="text-border">·</span>
              <button
                className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                onClick={() => setStep('request')}
                type="button"
              >
                <Trans>Change email</Trans>
              </button>
            </div>
            <p className="text-center text-muted-foreground text-xs leading-relaxed">
              <Trans>
                If an account exists, the code will arrive shortly. Check spam, or use Google if
                that is how you created your account.
              </Trans>
            </p>
          </FieldGroup>
        </form>
      )}
    </AuthLayout>
  )
}
